import PropTypes from "prop-types";
import { useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import PanelCard from "layouts/dutyEstimator/components/PanelCard";
import {
  SETTINGS_GROUPS,
  SETTINGS_FIELDS,
  NUMERIC_SETTINGS_KEYS,
  INPUT_SX,
  WHITE_BUTTON_SX,
} from "layouts/dutyEstimator/constants";

// The API stores fxRateUpdatedAt as an ISO timestamp, but <input type="datetime-local"> only
// accepts "YYYY-MM-DDTHH:mm" in local time.
const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export const buildSettingsForm = (data = {}) =>
  SETTINGS_FIELDS.reduce((form, field) => {
    const value = data?.[field.key];
    if (field.type === "switch") form[field.key] = value === undefined || value === null ? true : Boolean(value);
    else if (field.type === "datetime") form[field.key] = toDateTimeLocal(value);
    else form[field.key] = value ?? "";
    return form;
  }, {});

function SettingsPanel({ settings, onChange, onSave }) {
  // Only send what the admin actually touched, so an untouched blank field never overwrites a
  // saved value with 0.
  const [dirtyKeys, setDirtyKeys] = useState(new Set());

  const handleFieldChange = (key, value) => {
    setDirtyKeys((previous) => new Set(previous).add(key));
    onChange({ ...settings, [key]: value });
  };

  const handleSave = () => {
    const payload = {};

    dirtyKeys.forEach((key) => {
      const value = settings[key];
      if (typeof value === "boolean") {
        payload[key] = value;
      } else if (NUMERIC_SETTINGS_KEYS.includes(key)) {
        if (value !== "" && value !== null) payload[key] = Number(value);
      } else if (key === "fxRateUpdatedAt") {
        if (value) payload[key] = new Date(value).toISOString();
      } else if (value !== "" && value !== null) {
        payload[key] = value;
      }
    });

    onSave(payload);
    setDirtyKeys(new Set());
  };

  const renderField = (field) => {
    if (field.type === "switch") {
      return (
        <MDBox display="flex" alignItems="center" minHeight="44px">
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(settings[field.key])}
                onChange={(e) => handleFieldChange(field.key, e.target.checked)}
              />
            }
            label={
              <MDTypography variant="button" fontWeight="regular">
                {field.label}
              </MDTypography>
            }
          />
        </MDBox>
      );
    }

    if (field.type === "textarea") {
      return (
        <TextField
          fullWidth
          multiline
          minRows={3}
          label={field.label}
          value={settings[field.key] ?? ""}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          sx={INPUT_SX}
        />
      );
    }

    if (field.type === "feeType" || field.type === "networkChargeType") {
      const options =
        field.type === "feeType"
          ? [
              { value: "flat", label: "Flat amount (GHS)" },
              { value: "percentage", label: "% of Customs Value" },
            ]
          : [
              { value: "flat", label: "Flat amount (GHS)" },
              { value: "percentage_of_customs_value", label: "% of Customs Value" },
              { value: "percentage_of_fob", label: "% of FOB" },
            ];

      return (
        <TextField
          select
          fullWidth
          label={field.label}
          value={settings[field.key] ?? ""}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          sx={INPUT_SX}
          SelectProps={{ IconComponent: ArrowDropDownIcon }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        fullWidth
        type={field.type === "datetime" ? "datetime-local" : field.type}
        label={field.label}
        value={settings[field.key] ?? ""}
        onChange={(e) => handleFieldChange(field.key, e.target.value)}
        InputLabelProps={field.type === "datetime" ? { shrink: true } : undefined}
        sx={INPUT_SX}
      />
    );
  };

  return (
    <PanelCard
      title="Duty Estimator Settings"
      caption="Every rate and fee used by the calculator. Changes take effect immediately."
      action={
        <Button variant="contained" size="small" onClick={handleSave} sx={WHITE_BUTTON_SX}>
          Save Settings
        </Button>
      }
    >
      <MDBox pt={3} pb={3} px={3}>
        {SETTINGS_GROUPS.map((group, index) => (
          <MDBox key={group.title} mb={index === SETTINGS_GROUPS.length - 1 ? 0 : 4}>
            <MDTypography variant="h6" fontWeight="medium">
              {group.title}
            </MDTypography>
            {group.caption ? (
              <MDTypography variant="caption" color="text" display="block" mb={2}>
                {group.caption}
              </MDTypography>
            ) : (
              <MDBox mb={2} />
            )}

            <Grid container spacing={3}>
              {group.fields.map((field) => (
                <Grid item xs={12} sm={field.full ? 12 : 6} md={field.full ? 12 : 4} key={field.key}>
                  {renderField(field)}
                  {field.helper ? (
                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      {field.helper}
                    </MDTypography>
                  ) : null}
                </Grid>
              ))}
            </Grid>

            {index === SETTINGS_GROUPS.length - 1 ? null : <Divider sx={{ mt: 3, mb: 0 }} />}
          </MDBox>
        ))}
      </MDBox>
    </PanelCard>
  );
}

SettingsPanel.propTypes = {
  settings: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default SettingsPanel;
